import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button, CircularProgress, Divider, IconButton, Pagination, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, Menu, MenuItem, ListItemIcon } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useDataHora } from "../../../core/hooks/useDataHora";
import { useConfiguracoesPedidoContext } from "../../../pedidos/contexts/ConfiguracoesPedidoContext";
import { useClientes } from "../../hooks/useClientes";
import { useMascara } from "../../../core/hooks/useMascara";
import { useFetchClientes } from "../../hooks/useFetchClientes";
import { ICliente } from "../../interface/ICliente";
import { CadastrarEditarClienteModal } from "./containers/CadastrarEditarClienteModal";
import { CadastrarEditarEnderecoModal } from "./containers/CadastrarEditarEnderecoModal";
import { ExcluirClienteModal } from "./containers/ExcluirClienteModal";
import { ButtonsContainer, FiltrosContainer, HeaderContainer, Loading, NenhumCliente, PageContainer, TableContainer } from "./styles";
import { useAutenticacaoContext } from '../../../core/contexts/AutenticacaoContext';
import { useNavigate } from 'react-router-dom';
import { ClienteTableRow } from '../../components/ClienteTableRow';
import { DetalhesPedidosDrawer } from '../../components/DetalhesPedidosDrawer';

export const ListarClientes = () => {
    const [clienteEdicao, setClienteEdicao] = useState<ICliente | undefined>(undefined);
    const [clienteExclusao, setClienteExclusao] = useState<ICliente | undefined>(undefined);
    const [clienteEndereco, setClienteEndereco] = useState<ICliente | undefined>(undefined);
    const [clienteDetalhes, setClienteDetalhes] = useState<ICliente | null>(null);

    // Estados para seleção múltipla
    const [clientesSelecionados, setClientesSelecionados] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    // Estado para o drawer de detalhes
    const [openDetalhes, setOpenDetalhes] = useState(false);

    const snackbar = useSnackbar();

    const [openCadastrarEditar, setOpenCadastrarEditar] = useState(false);
    const handleOpenCadastrarEditar = () => setOpenCadastrarEditar(true);
    const handleCloseCadastrarEditar = () => setOpenCadastrarEditar(false);

    const [openExcluir, setOpenExcluir] = useState(false);
    const handleOpenExcluir = () => setOpenExcluir(true);
    const handleCloseExcluir = () => setOpenExcluir(false);

    const [openEndereco, setOpenEndereco] = useState(false);
    const handleOpenEndereco = () => setOpenEndereco(true);
    const handleCloseEndereco = () => setOpenEndereco(false);

    const { salvar, loading, loadingExportar, excluir, excluirEmLote, exportar } = useClientes();
    const {
        dados,
        handlePaginaChange,
        handleRefresh,
        handleLimpar,
        handleNomeChange,
        handleObservacaoChange,
        handleTelefoneChange,
        isLoading,
        nome,
        observacao,
        telefone,
        handleFiltrar,
        filtrosAplicados
    } = useFetchClientes();
    const { dataHoraBr } = useDataHora();

    // Funções para seleção múltipla
    const toggleClienteSelecionado = (uuid: string) => {
        setClientesSelecionados(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const limparSelecao = () => {
        setClientesSelecionados([]);
    };

    const abrirAcoes = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    // Funções para ações em lote
    const handleEditarClienteSelecionado = () => {
        if (clientesSelecionados.length === 1) {
            const cliente = dados?.itens.find((c: ICliente) => c.uuid === clientesSelecionados[0]);
            if (cliente) {
                setClienteEdicao(cliente);
                setOpenCadastrarEditar(true);
                limparSelecao();
            }
        }
        fecharAcoes();
    };

    const handleEditarEnderecoClienteSelecionado = () => {
        if (clientesSelecionados.length === 1) {
            const cliente = dados?.itens.find((c: ICliente) => c.uuid === clientesSelecionados[0]);
            if (cliente) {
                setClienteEndereco(cliente);
                setOpenEndereco(true);
                limparSelecao();
            }
        }
        fecharAcoes();
    };

    const handleExcluirClientesSelecionados = async () => {
        if (clientesSelecionados.length > 0) {
            const resultado = await excluirEmLote(clientesSelecionados);
            if (resultado) {
                handleRefresh();
                limparSelecao();
            }
        }
        fecharAcoes();
    };

    const possuiFiltrosAplicados = Boolean(filtrosAplicados.nome || filtrosAplicados.observacao || filtrosAplicados.telefone);

    const handleExportarClientes = async () => {
        fecharAcoes();
        await exportar(filtrosAplicados);
    };

    // Função para abrir detalhes do cliente
    const handleAbrirDetalhes = (cliente: ICliente) => {
        setClienteDetalhes(cliente);
        setOpenDetalhes(true);
    };

    const handleFecharDetalhes = () => {
        setOpenDetalhes(false);
        setClienteDetalhes(null);
    };

    const handleCancelarEditarCadastrar = () => {
        handleCloseCadastrarEditar();
        setClienteEdicao(undefined);
    }

    const { formatarTelefoneParaPadrao } = useMascara();

    const handleCadastrarCliente = async ({ nome, telefone, cpf, observacao }: FieldValues) => {
        if (loading) {
            return;
        }

        // Formatar telefone antes de salvar
        const telefoneFormatado = formatarTelefoneParaPadrao(telefone);

        const clienteCadastrado = await salvar({ nome, telefone: telefoneFormatado, cpf, observacao });

        if (!clienteCadastrado) {
            return snackbar("Ocorreu um erro ao cadastrar o cliente", { severity: "error" })
        }

        handleRefresh();
        handleCancelarEditarCadastrar();
        return snackbar("Cliente cadastrado com sucesso", { severity: "success" })
    }

    const handleEditarCliente = async ({ nome, telefone, cpf, observacao }: FieldValues) => {
        if (loading) {
            return;
        }

        // Formatar telefone antes de salvar
        const telefoneFormatado = formatarTelefoneParaPadrao(telefone);
        const dados: ICliente = { nome, telefone: telefoneFormatado, cpf, observacao };

        if (clienteEdicao?.uuid) {
            dados.uuid = clienteEdicao.uuid
        }

        const clienteEditado = await salvar(dados);
        if (!clienteEditado) {
            return snackbar("Ocorreu um erro ao editar o cliente", { severity: "error" })
        }

        handleRefresh();
        handleCancelarEditarCadastrar();
        return snackbar("Cliente editado com sucesso", { severity: "success" })
    }

    const handleExcluirCliente = async (clienteUuid: string) => {
        if (loading) {
            return;
        }

        const clienteExcluido = await excluir(clienteUuid);
        if (!clienteExcluido) {
            return snackbar("Ocorreu um erro ao excluir o cliente", { severity: "error" })
        }

        handleRefresh();
        handleCancelarExcluir();
        return snackbar("Cliente excluído com sucesso", { severity: "success" })
    }

    const handleAbrirEdicaoCliente = (cliente: ICliente) => {
        setClienteEdicao(cliente);
        setOpenCadastrarEditar(true);
    }

    const handleAbrirExclusaoCliente = (cliente: ICliente) => {
        setClienteExclusao(cliente);
        handleOpenExcluir();
    }

    const handleCancelarExcluir = () => {
        setClienteExclusao(undefined);
        handleCloseExcluir();
    }

    const handleAbrirEndereco = (cliente: ICliente) => {
        setClienteEndereco(cliente);
        handleOpenEndereco();
    }

    const handleCancelarEndereco = () => {
        setClienteEndereco(undefined);
        handleCloseEndereco();
    }

    const handleSalvar = () => {
        handleCloseEndereco();
        handleRefresh();
    }

    const navigate = useNavigate();
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();
    useEffect(() => {
        if(perfil && !verificarAcessoFuncionalidade("Gestão de clientes")) {
            navigate("/nao-autorizado")
        }
    }, [perfil])
    
    return (
        <PageContainer>
            <CadastrarEditarClienteModal
                open={openCadastrarEditar}
                handleCadastrarCliente={handleCadastrarCliente}
                handleEditarCliente={handleEditarCliente}
                handleCancelar={handleCancelarEditarCadastrar}
                loading={loading}
                dadosCliente={clienteEdicao}
            />
            <ExcluirClienteModal
                cliente={clienteExclusao}
                open={openExcluir}
                excluir={handleExcluirCliente}
                cancelar={handleCancelarExcluir}
            />
            <CadastrarEditarEnderecoModal
                cliente={clienteEndereco}
                open={openEndereco}
                salvar={handleSalvar}
                cancelar={handleCancelarEndereco}
            />
            <DetalhesPedidosDrawer
                cliente={clienteDetalhes}
                open={openDetalhes}
                onClose={handleFecharDetalhes}
            />
            <FiltrosContainer>
                <Typography variant='h6'>Clientes</Typography>
                <Box className="filtros-clientes">
                    <TextField
                        placeholder="Nome"
                        type="text"
                        value={nome}
                        size='small'
                        onChange={handleNomeChange}
                        className='input-cliente'
                    />
                    <TextField
                        placeholder="Observação"
                        type="text"
                        value={observacao}
                        size='small'
                        onChange={handleObservacaoChange}
                        className='input-cliente'
                    />
                    <TextField
                        placeholder="Telefone"
                        type="text"
                        value={telefone}
                        size='small'
                        sx={{ width: "200px" }}
                        onChange={handleTelefoneChange}
                        className='input-cliente'
                    />
                    <ButtonsContainer>
                        <Button
                            variant="outlined"
                            onClick={handleLimpar}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleFiltrar}
                        >
                            Filtrar
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Button
                            variant="contained"
                            endIcon={
                                <ArrowDropDown sx={{ transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                            }
                            onClick={abrirAcoes}
                        >
                            Ações
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={openAcoes}
                            onClose={fecharAcoes}
                        >
                            {clientesSelecionados.length > 0 && (
                                <MenuItem disabled>
                                    <Typography variant="inherit" noWrap>
                                        {clientesSelecionados.length} cliente{clientesSelecionados.length !== 1 ? 's' : ''} selecionado{clientesSelecionados.length !== 1 ? 's' : ''}
                                    </Typography>
                                </MenuItem>
                            )}
                            {clientesSelecionados.length > 0 && <Divider />}
                            <MenuItem
                                onClick={handleEditarClienteSelecionado}
                                disabled={clientesSelecionados.length !== 1}
                            >
                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">Editar dados</Typography>
                            </MenuItem>
                            <MenuItem
                                onClick={handleEditarEnderecoClienteSelecionado}
                                disabled={clientesSelecionados.length !== 1}
                            >
                                <ListItemIcon><LocationOnIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">Editar endereço</Typography>
                            </MenuItem>
                            <MenuItem
                                onClick={handleExcluirClientesSelecionados}
                                disabled={clientesSelecionados.length === 0}
                            >
                                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">Excluir</Typography>
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleExportarClientes} disabled={loadingExportar}>
                                <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">
                                    {loadingExportar
                                        ? "Exportando..."
                                        : possuiFiltrosAplicados
                                            ? "Exportar clientes filtrados"
                                            : "Exportar clientes"}
                                </Typography>
                            </MenuItem>
                        </Menu>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Tooltip title="Cadastre um novo cliente">
                            <Button
                                onClick={handleOpenCadastrarEditar}
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Novo
                            </Button>
                        </Tooltip>
                    </ButtonsContainer>
                </Box>
            </FiltrosContainer>
            <Paper sx={{
                padding: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                margin: "0 auto",
                width: "85%"
            }}>

                <TableContainer>
                    {isLoading
                        ? (
                            <Loading>
                                <CircularProgress />
                            </Loading>
                        )
                        : dados && dados.itens.length ? (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                        </TableCell>
                                        <TableCell align="left" width="25%">Nome</TableCell>
                                        <TableCell align="left" width="25%">Telefone</TableCell>
                                        <TableCell align="left" width="25%">Observação</TableCell>
                                        <TableCell align="left" width="20%">Cadastrado às</TableCell>
                                        <TableCell align="center" width="5%">Detalhes</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.itens.map((cliente: ICliente) => (
                                        <ClienteTableRow
                                            key={cliente.uuid || 'temp-key'}
                                            cliente={cliente}
                                            selecionado={cliente.uuid ? clientesSelecionados.includes(cliente.uuid) : false}
                                            toggleSelecionado={toggleClienteSelecionado}
                                            onDetalhes={handleAbrirDetalhes}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <NenhumCliente>
                                <Typography>
                                    Nenhum cliente cadastrado
                                </Typography>
                            </NenhumCliente>
                        )}
                </TableContainer>
                {dados && (
                    <Pagination
                        count={dados.quantidadeTotalPaginas}
                        page={dados.paginaAtual}
                        onChange={handlePaginaChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        siblingCount={2}
                        boundaryCount={2}
                        sx={{ marginTop: "1rem" }}
                    />
                )}
            </Paper>
        </PageContainer>
    );
}